import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { MeasurementTool } from 'src/components/viewers/SheetsViewerV2/autodesk/tools/MeasurementTool';
import { ActiveTool } from 'src/components/viewers/SheetsViewerV2/enums/ActiveTool';
import { ActiveSheetData } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheet';
import { useCalibration } from 'src/components/viewers/SheetsViewerV2/hooks/useCalibration';
import { AggregatedView } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetsAggregatedView';
import { useSnapper } from 'src/components/viewers/SheetsViewerV2/hooks/useSnapper';
import { ViewerExtension } from 'src/components/viewers/common/utils/autodesk';
import Edit2D = Autodesk.Extensions.Edit2D;

type Props = {
  activeTool: ActiveTool;
  setActiveTool: Dispatch<SetStateAction<ActiveTool>>;
  activeSheetData: ActiveSheetData | null;
  viewer: AggregatedView['viewer'] | undefined;
  calibration: ReturnType<typeof useCalibration>['existing'];
};

export function useMeasurementTool({
  activeTool,
  setActiveTool,
  activeSheetData,
  viewer,
  calibration,
}: Props) {
  const { snapper } = useSnapper(viewer);
  const [edit2D, setEdit2D] = useState<Edit2D | null>(null);

  const isMeasuring = useMemo(
    () => activeTool === ActiveTool.Measurement && !!calibration,
    [activeTool, calibration]
  );

  // Callback to disable the tool (called when ESC is pressed)
  const disableTool = useCallback(() => {
    setActiveTool((prev) =>
      prev === ActiveTool.Measurement ? ActiveTool.Default : prev
    );
  }, [setActiveTool]);

  // Get Edit2D extension
  useEffect(() => {
    if (!viewer) return;

    const ext = viewer.getExtension(ViewerExtension.Edit2D) as Edit2D;
    if (ext && !ext.defaultContext) {
      ext.registerDefaultTools();
    }
    setEdit2D(ext);
  }, [viewer]);

  useEffect(() => {
    if (!viewer || !snapper || !calibration || !activeSheetData || !edit2D)
      return;

    const tool = new MeasurementTool(
      calibration,
      activeSheetData,
      snapper,
      viewer,
      edit2D,
      disableTool
    );

    if (isMeasuring) {
      viewer.toolController.registerTool(tool);
      viewer.disableSelection(true);
      viewer.disableHighlight(true);
      viewer.setContextMenu(null);
      viewer.toolController.activateTool(tool.getName());
    }

    return () => {
      try {
        viewer.toolController.deactivateTool(tool.getName());
      } catch {
        // Tool may not be activated
      }
      viewer.toolController.deregisterTool(tool);
    };
  }, [
    isMeasuring,
    snapper,
    calibration,
    viewer,
    activeSheetData,
    edit2D,
    disableTool,
  ]);

  return { isMeasuring };
}
